import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import type { AssetType } from '@k-ucap/shared';
import { useSessionStore } from '@/stores/useSessionStore.js';

export interface CommandItem {
  id: string;
  type: 'menu' | 'action' | 'asset' | 'favorite';
  label: string;
  description?: string;
  href?: string;
  action?: () => void;
  icon?: string;
}

export interface UseCommandBarOptions {
  favorites?: { id: string; assetType: AssetType; name: string; href: string }[];
  onDemoStart?: () => void;
  onSelfTest?: () => void;
  onNewSimulationTest?: () => void;
  onNewTestSuite?: () => void;
}

const defaultMenuItems: CommandItem[] = [
  { id: 'menu-dashboard', type: 'menu', label: '대시보드', href: '/dashboard', icon: 'dashboard' },
  { id: 'menu-generator', type: 'menu', label: '메시지 생성기', href: '/generator', icon: 'message' },
  { id: 'menu-scenario', type: 'menu', label: '시험 시나리오', href: '/scenario', icon: 'scenario' },
  { id: 'menu-analyzer', type: 'menu', label: '메시지 분석기', href: '/analyzer', icon: 'analyzer' },
  { id: 'menu-gis', type: 'menu', label: 'GIS 전시기', href: '/gis', icon: 'gis' },
  { id: 'menu-messages', type: 'menu', label: '메시지 로그', href: '/messages', icon: 'log' },
  { id: 'menu-reports', type: 'menu', label: '보고서', href: '/reports', icon: 'report' },
  { id: 'menu-integration', type: 'menu', label: '연동 관리', href: '/integration', icon: 'integration' },
  { id: 'menu-settings', type: 'menu', label: '설정', href: '/settings', icon: 'settings' },
];

const defaultActions: CommandItem[] = [
  { id: 'action-new-simulation', type: 'action', label: '새 시뮬레이션 테스트', icon: 'add' },
  { id: 'action-new-suite', type: 'action', label: '새 테스트 스위트', icon: 'add' },
  { id: 'action-demo', type: 'action', label: '데모 실행', icon: 'play' },
  { id: 'action-self-test', type: 'action', label: '자체 진단', icon: 'check' },
];

export const useCommandBar = (options: UseCommandBarOptions) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const currentSession = useSessionStore((s) => s.currentSession);

  const items = useMemo(() => {
    const favoriteItems: CommandItem[] = (options.favorites ?? []).map((item) => ({
      id: `fav-${item.id}`,
      type: 'favorite',
      label: item.name,
      description: `Favorite ${item.assetType}`,
      href: item.href,
      icon: 'star',
    }));
    const sessionItems: CommandItem[] = currentSession
      ? [
          {
            id: 'session-workbench',
            type: 'action',
            label: '현재 세션 Workbench 열기',
            description: `Session ${currentSession.id.slice(0, 16)}…`,
            href: `/scenario?sessionId=${currentSession.id}`,
            icon: 'scenario',
          },
          {
            id: 'session-analyzer',
            type: 'action',
            label: '현재 세션 Analyzer 열기',
            href: `/analyzer?sessionId=${currentSession.id}`,
            icon: 'analyzer',
          },
          {
            id: 'session-gis',
            type: 'action',
            label: '현재 세션 GIS 열기',
            href: `/gis?sessionId=${currentSession.id}`,
            icon: 'gis',
          },
          {
            id: 'session-reports',
            type: 'action',
            label: '현재 세션 Report 열기',
            href: `/reports?sessionId=${currentSession.id}`,
            icon: 'report',
          },
        ]
      : [];
    return [...sessionItems, ...defaultMenuItems, ...defaultActions, ...favoriteItems];
  }, [options.favorites, currentSession]);

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (item) => item.label.toLowerCase().includes(q) || (item.description?.toLowerCase().includes(q) ?? false)
    );
  }, [items, query]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query, filteredItems.length]);

  const execute = useCallback(
    (item: CommandItem) => {
      if (item.href) {
        window.location.href = item.href;
      } else if (item.action) {
        item.action();
      } else if (item.id === 'action-demo') {
        options.onDemoStart?.();
      } else if (item.id === 'action-self-test') {
        options.onSelfTest?.();
      } else if (item.id === 'action-new-simulation') {
        options.onNewSimulationTest?.();
      } else if (item.id === 'action-new-suite') {
        options.onNewTestSuite?.();
      }
      setOpen(false);
      setQuery('');
    },
    [options]
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, filteredItems.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const item = filteredItems[selectedIndex];
        if (item) execute(item);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    },
    [filteredItems, selectedIndex, execute]
  );

  return {
    open,
    setOpen,
    query,
    setQuery,
    selectedIndex,
    setSelectedIndex,
    items: filteredItems,
    execute,
    handleKeyDown,
    inputRef,
  };
};
